<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
    <div class="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-lg shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[90vh] overflow-hidden">

      <!-- Header -->
      <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
        <h3 class="text-base font-bold text-slate-800 dark:text-slate-100">
          {{ form.id ? 'Editar Producto / Servicio' : 'Nuevo Producto / Servicio' }}
        </h3>
        <button @click="$emit('close')" class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
          <X class="w-5 h-5" />
        </button>
      </div>

      <!-- Body Form -->
      <form @submit.prevent="handleSubmit" class="p-6 overflow-y-auto space-y-4">

        <!-- Imagen -->
        <div>
          <label class="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Fotografía del Producto</label>
          <div class="flex items-center gap-4">
            <div class="w-20 h-20 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 flex items-center justify-center overflow-hidden relative">
              <img v-if="form.photo_url" :src="form.photo_url" class="w-full h-full object-cover" />
              <Package v-else class="w-8 h-8 text-slate-400" />
              <div v-if="isUploading" class="absolute inset-0 bg-black/50 flex items-center justify-center">
                <Loader2 class="w-5 h-5 text-white animate-spin" />
              </div>
            </div>
            <div>
              <input type="file" ref="fileInput" @change="handleImage" accept="image/*" class="hidden" />
              <button type="button" @click="$refs.fileInput.click()" :disabled="isUploading" class="px-3 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-lg border border-slate-300 dark:border-slate-600 flex items-center gap-2">
                <Upload class="w-3.5 h-3.5" /> Subir y Comprimir Foto
              </button>
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <!-- Tipo -->
          <div>
            <label class="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Tipo</label>
            <select v-model="form.type" class="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-200">
              <option value="Product">Producto</option>
              <option value="Service">Servicio</option>
            </select>
          </div>

          <!-- Agente -->
          <div>
            <label class="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Agente</label>
            <select v-model="form.ai_character" required class="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-200">
              <option :value="null" disabled>Seleccionar agente</option>
              <option v-for="agent in agents" :key="agent.id" :value="agent.id">{{ agent.name }}</option>
            </select>
          </div>
        </div>

        <!-- Nombre -->
        <div>
          <label class="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Nombre</label>
          <input v-model="form.name" required placeholder="Ej. Camiseta Oversize Negra" class="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-200" />
        </div>

        <!-- Precios y Stock -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Precio</label>
            <input v-model.number="form.price" type="number" step="0.01" required class="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-200" />
          </div>
          <div v-if="form.type === 'Product'">
            <label class="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Stock Disponible</label>
            <input v-model.number="form.stock" type="number" placeholder="Opcional" class="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-200" />
          </div>
        </div>

        <!-- Descripción -->
        <div>
          <label class="text-[10px] font-bold text-slate-500 uppercase mb-1 block">Descripción para el agente</label>
          <textarea v-model="form.description" rows="3" placeholder="Detalles de tallas, materiales o servicios que el bot usará para responder..." class="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-700 dark:text-slate-200"></textarea>
        </div>

        <!-- Disponibilidad -->
        <div class="flex items-center gap-2 pt-2">
          <input type="checkbox" id="available" v-model="form.is_available" :true-value="1" :false-value="0" class="rounded text-indigo-600 focus:ring-indigo-500" />
          <label for="available" class="text-sm font-medium text-slate-700 dark:text-slate-300">Disponible para venta por agente</label>
        </div>

        <!-- Footer -->
        <div class="pt-4 border-t border-slate-200 dark:border-slate-800 flex justify-end gap-3">
          <button type="button" @click="$emit('close')" class="px-4 py-2 text-sm text-slate-600 dark:text-slate-400">Cancelar</button>
          <button type="submit" :disabled="isSaving || isUploading" class="px-5 py-2 text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl flex items-center gap-2 disabled:opacity-50">
            <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
            <Save v-else class="w-4 h-4" /> Guardar
          </button>
        </div>
      </form>

    </div>
  </div>
</template>

<script setup>
import { reactive, watch, ref } from 'vue';
import { X, Upload, Package, Loader2, Save } from 'lucide-vue-next';
import { useUpload } from '@/composables/useUpload';
import { useToast } from '@/composables/useToast';
import apiClient from '@/api/client';

const props = defineProps({
  show: Boolean,
  productToEdit: Object,
  agents: { type: Array, default: () => [] }
});
const emit = defineEmits(['close', 'saved']);

const { uploadFile, deleteFile, isUploading } = useUpload();
const { showSuccess, showError } = useToast();
const isSaving = ref(false);
const fileInput = ref(null);

const form = reactive({
  id: null,
  ai_character: null,
  type: 'Product',
  name: '',
  description: '',
  price: 0,
  stock: null,
  is_available: 1,
  photo_url: ''
});

watch(() => props.show, (val) => {
  if (val) {
    if (props.productToEdit) {
      Object.assign(form, props.productToEdit);
    } else {
      Object.assign(form, {
        id: null,
        ai_character: props.agents[0]?.id || null,
        type: 'Product',
        name: '',
        description: '',
        price: 0,
        stock: null,
        is_available: 1,
        photo_url: ''
      });
    }
  }
});

const handleImage = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const oldPhoto = form.photo_url;
  const url = await uploadFile(file);
  if (url) {
    if (oldPhoto) await deleteFile(oldPhoto);
    form.photo_url = url;
    showSuccess('Foto subida y optimizada');
  }
};

const handleSubmit = async () => {
  isSaving.value = true;
  try {
    const payload = { ...form };
    if (!payload.id) delete payload.id;

    if (form.id) {
      await apiClient.put(`/products_services/${form.id}`, payload);
      showSuccess('Producto actualizado');
    } else {
      await apiClient.post('/products_services', payload);
      showSuccess('Producto creado correctamente');
    }
    emit('saved');
    emit('close');
  } catch (err) {
    showError('Error al guardar el producto');
  } finally {
    isSaving.value = false;
  }
};
</script>